/** @format */

import {safeDivide} from 'helpers/numberHelpers';

import {DownloadMetricsFromQuery, SensorsFromQuery} from './types';

export const sumDownloadMetrics = (data: DownloadMetricsFromQuery) => {
  return (
    data?.reduce((acc, cur) => {
      return cur?.downloads ? acc + cur.downloads : acc;
    }, 0) ?? 0
  );
};

export const sumDirectClickCount = (data: DownloadMetricsFromQuery) =>
  data?.reduce((acc, cur) => {
    return cur?.directClickCount ? acc + cur.directClickCount : acc;
  }, 0) ?? 0;

export const sumPageOpenCount = (data: DownloadMetricsFromQuery) =>
  data?.reduce((acc, cur) => {
    return cur?.pageOpenCount ? acc + cur.pageOpenCount : acc;
  }, 0) ?? 0;

export const foundValue = (sensors: SensorsFromQuery) =>
  sensors?.reduce((acc, cur) => {
    return cur?.found ? acc + cur.found : acc;
  }, 0) ?? 0;

export const fixedValue = (sensors: SensorsFromQuery) =>
  sensors?.reduce((acc, cur) => {
    return cur?.fixed ? acc + cur.fixed : acc;
  }, 0) ?? 0;

export const calculateFixedPercent = (sensors: SensorsFromQuery) => {
  const found = foundValue(sensors);
  const fixed = fixedValue(sensors);
  return found > 0 ? safeDivide(fixed, found) * 100 : 0;
};

export const resolvedAvgValue = (sensors: SensorsFromQuery) => {
  if (!sensors?.length) {
    return 0;
  }

  const resolvedAvgSum =
    sensors.reduce((acc, cur) => {
      return cur?.resolvedAvg ? acc + cur.resolvedAvg : acc;
    }, 0) ?? 0;

  return resolvedAvgSum / sensors.length;
};

export const resolvedMinValue = (sensors: SensorsFromQuery) => {
  if (!sensors?.length) {
    return 0;
  }

  const allResolvedMin = sensors
    .filter(sensor => !sensor?.isDisabled)
    .map(sensor => sensor?.resolvedMin)
    .filter(sensor => typeof sensor === 'number');

  return Math.max(...allResolvedMin);
};

export const resolvedMaxValue = (sensors: SensorsFromQuery) => {
  if (!sensors?.length) {
    return 0;
  }

  const allResolvedMax = sensors
    .filter(sensor => !sensor?.isDisabled)
    .map(sensor => sensor?.resolvedMax)
    .filter(sensor => typeof sensor === 'number');

  return Math.max(...allResolvedMax);
};

export type BarColor = 'green' | 'red' | 'yellow';

const GOOD_AMOUNT_OF_DOWNLOADS = 3;
const OPTIMAL_AMOUNT_OF_DOWNLOADS = 2;
const BAD_AMOUNT_OF_RESOLVED_SENSORS = 14;
const OPTIMAL_AMOUNT_OF_RESOLVED_SENSORS = 7;

export const resolvedBarColor = (amount: number): BarColor =>
  amount >= BAD_AMOUNT_OF_RESOLVED_SENSORS
    ? 'red'
    : amount >= OPTIMAL_AMOUNT_OF_RESOLVED_SENSORS
      ? 'yellow'
      : 'green';

export const downloadsBarColor = (downloads: number, amountOfWeeks: number): BarColor =>
  downloads >= GOOD_AMOUNT_OF_DOWNLOADS * amountOfWeeks
    ? 'green'
    : downloads >= OPTIMAL_AMOUNT_OF_DOWNLOADS * amountOfWeeks
      ? 'yellow'
      : 'red';

// Can exceed 100 (e.g. parent aggregate values); ResolvedLine clamps visually via maxWidth: 100px.
export const barWidth = (amount: number, max: number): number => (max ? (100 * amount) / max : 0);

export const isNotNull = <T>(value: T): value is NonNullable<T> => value != null;
